import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X, Radio, Search, Monitor, Tv, MoreHorizontal, Calendar, Newspaper, MapPin, Info, Headphones, Podcast, Smartphone, User, LogOut, Settings, MonitorSmartphone } from 'lucide-react';
import { Logo } from './Logo';
import { api } from '@/api';
import { useTVMode } from '@/contexts/TVModeContext';
import { useViewerAuth } from '@/contexts/ViewerAuthContext';
import { resolveMediaUrl } from '@/utils/helpers';

export const Navigation = ({ sports, leagues }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [hasLiveGames, setHasLiveGames] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownTimerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { tvMode, toggleTvMode, device } = useTVMode();
  const { viewer, logout } = useViewerAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);

  // Check for live games periodically
  useEffect(() => {
    const checkLive = async () => {
      try {
        const res = await api.getLiveTVGames();
        setHasLiveGames(res.data.is_live || false);
      } catch { /* silent */ }
    };
    checkLive();
    const interval = setInterval(checkLive, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close everything on route change
  useEffect(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults(null);
    setOpenDropdown(null);
    setMobileMenuOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  // Close search on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.search(searchQuery);
        setSearchResults(res.data);
      } catch {
        setSearchResults(null);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, []);

  const handleSearchSelect = useCallback((path) => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults(null);
    navigate(path);
  }, [navigate]);

  const totalResults = searchResults
    ? (searchResults.teams?.length || 0) + (searchResults.leagues?.length || 0) + (searchResults.games?.length || 0)
    : 0;

  const getLeaguesForSport = (sportId) => {
    return leagues?.filter(l => l.sport_id === sportId) || [];
  };

  // Separate GAA sports from others
  const gaaSports = sports.filter(s => s.slug === 'gaelic-football' || s.slug === 'hurling-camogie');
  const otherSports = sports.filter(s => s.slug !== 'gaelic-football' && s.slug !== 'hurling-camogie');
  const isGAAActive = gaaSports.some(s => location.pathname.includes(s.slug));

  // Dropdown open/close with delay to prevent flickering
  const handleDropdownEnter = (id) => {
    clearTimeout(dropdownTimerRef.current);
    setOpenDropdown(id);
  };
  const handleDropdownLeave = () => {
    dropdownTimerRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  const toggleMobileSection = (sectionId) => {
    setMobileExpanded(prev => prev === sectionId ? null : sectionId);
  };

  // ============== MEGA DROPDOWN FOR SPORTS ==============
  const SportMegaDropdown = ({ sportId, sportLeagues }) => {
    if (sportLeagues.length === 0) return null;
    return (
      <div 
        className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50"
        onMouseEnter={() => handleDropdownEnter(sportId)}
        onMouseLeave={handleDropdownLeave}
      >
        <div className="bg-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-700/50 py-3 px-2 min-w-[240px]">
          <div className="px-3 pb-2 mb-2 border-b border-gray-700/50">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Competitions</span>
          </div>
          <div className="space-y-0.5">
            {sportLeagues.map(league => (
              <Link
                key={league.id}
                to={`/league/${league.slug}`}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                onClick={() => setOpenDropdown(null)}
                data-testid={`nav-league-${league.slug}`}
              >
                {league.logo ? (
                  <img src={resolveMediaUrl(league.logo)} alt="" className="w-5 h-5 rounded object-cover" />
                ) : (
                  <div className="w-5 h-5 rounded bg-sky-800/50 flex items-center justify-center text-[10px] font-bold text-sky-300">
                    {league.name?.[0]}
                  </div>
                )}
                {league.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============== GAA MEGA DROPDOWN ==============
  const GAAMegaDropdown = () => (
    <div 
      className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50"
      onMouseEnter={() => handleDropdownEnter('gaa')}
      onMouseLeave={handleDropdownLeave}
    >
      <div className="bg-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-700/50 py-3 px-2 min-w-[280px]">
        {gaaSports.map(sport => {
          const sportLeagues = getLeaguesForSport(sport.id);
          return (
            <div key={sport.id} className="mb-2 last:mb-0">
              <Link
                to={`/sport/${sport.slug}`}
                className="block px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-white transition-colors"
                onClick={() => setOpenDropdown(null)}
              >
                {sport.name}
              </Link>
              {sportLeagues.length > 0 && (
                <div className="space-y-0.5 mt-1">
                  {sportLeagues.map(league => (
                    <Link
                      key={league.id}
                      to={`/league/${league.slug}`}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                      onClick={() => setOpenDropdown(null)}
                      data-testid={`nav-league-${league.slug}`}
                    >
                      {league.logo ? (
                        <img src={resolveMediaUrl(league.logo)} alt="" className="w-5 h-5 rounded object-cover" />
                      ) : (
                        <div className="w-5 h-5 rounded bg-emerald-800/50 flex items-center justify-center text-[10px] font-bold text-emerald-300">
                          {league.name?.[0]}
                        </div>
                      )}
                      {league.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ============== MORE DROPDOWN ==============
  const MoreDropdown = () => (
    <div 
      className="absolute top-full right-0 pt-3 z-50"
      onMouseEnter={() => handleDropdownEnter('more')}
      onMouseLeave={handleDropdownLeave}
    >
      <div className="bg-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-700/50 py-2 w-56">
        <Link to="/find-game" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setOpenDropdown(null)}>
          <MapPin className="w-4 h-4 text-sky-400" /> Find a Stream
        </Link>
        <Link to="/about" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setOpenDropdown(null)}>
          <Info className="w-4 h-4 text-sky-400" /> About Us
        </Link>
        <div className="border-t border-gray-700/50 my-1.5" />
        <Link to="/radio" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setOpenDropdown(null)}>
          <Headphones className="w-4 h-4 text-purple-400" /> NLS Radio
        </Link>
        <a href="https://open.spotify.com/show/newlogicalsports" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setOpenDropdown(null)}>
          <Podcast className="w-4 h-4 text-green-400" /> Podcast
        </a>
        <div className="border-t border-gray-700/50 my-1.5" />
        <Link to="/devices" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setOpenDropdown(null)}>
          <Smartphone className="w-4 h-4 text-amber-400" /> Manage Devices
        </Link>
        <Link to="/preferences" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setOpenDropdown(null)}>
          <Monitor className="w-4 h-4 text-amber-400" /> Preferences
        </Link>
      </div>
    </div>
  );

  // Active link style helper
  const navLinkClass = (isActive) =>
    `flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold tracking-wide transition-all rounded-md ${
      isActive
        ? 'text-white bg-white/10'
        : 'text-gray-300 hover:text-white hover:bg-white/5'
    }`;

  return (
    <nav className="sticky top-0 z-50" style={{ backgroundColor: '#1a3a52' }}>
      <div className="max-w-[1400px] mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
            <Logo className="h-10 w-auto" />
          </Link>

          {/* Desktop Nav — Primary Items */}
          <div className="hidden md:flex items-center gap-1 ml-6">
            {/* Live */}
            <Link to="/live" className={navLinkClass(location.pathname === '/live')} data-testid="nav-live">
              <Radio className="w-4 h-4" />
              Live
              {hasLiveGames && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
              )}
            </Link>

            {/* Divider */}
            <div className="w-px h-5 bg-white/15 mx-1" />

            {/* Sport Links with Mega Dropdowns */}
            {otherSports.map(sport => {
              const sportLeagues = getLeaguesForSport(sport.id);
              const isActive = location.pathname.includes(sport.slug);
              return (
                <div
                  key={sport.id}
                  className="relative"
                  onMouseEnter={() => handleDropdownEnter(sport.id)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <Link
                    to={`/sport/${sport.slug}`}
                    className={navLinkClass(isActive)}
                    data-testid={`nav-${sport.slug}`}
                  >
                    {sport.name}
                    {sportLeagues.length > 0 && (
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === sport.id ? 'rotate-180' : ''}`} />
                    )}
                  </Link>
                  {sportLeagues.length > 0 && openDropdown === sport.id && (
                    <SportMegaDropdown sportId={sport.id} sportLeagues={sportLeagues} />
                  )}
                </div>
              );
            })}

            {/* GAA Combined */}
            {gaaSports.length > 0 && (
              <div
                className="relative"
                onMouseEnter={() => handleDropdownEnter('gaa')}
                onMouseLeave={handleDropdownLeave}
              >
                <button className={navLinkClass(isGAAActive)} data-testid="nav-gaa">
                  GAA
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === 'gaa' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'gaa' && <GAAMegaDropdown />}
              </div>
            )}

            {/* Divider */}
            <div className="w-px h-5 bg-white/15 mx-1" />

            {/* Schedule & News — direct links */}
            <Link to="/fixtures" className={navLinkClass(location.pathname === '/fixtures')} data-testid="nav-schedule">
              <Calendar className="w-4 h-4" />
              Schedule
            </Link>
            <Link to="/news" className={navLinkClass(location.pathname === '/news' || location.pathname.startsWith('/news/'))} data-testid="nav-news">
              <Newspaper className="w-4 h-4" />
              News
            </Link>
          </div>

          {/* Right Side — TV Mode, Search, More, Mobile */}
          <div className="flex items-center gap-1">
            {/* Exit TV Mode */}
            {tvMode && (
              <button onClick={toggleTvMode} className="tv-toggle-btn active" data-testid="tv-mode-exit" data-tv-focusable="true" title="Exit TV Mode">
                <Monitor className="w-4 h-4" />
                <span className="hidden sm:inline">Exit TV</span>
              </button>
            )}

            {/* Search */}
            <div className="relative" ref={searchRef}>
              {searchOpen ? (
                <div className="flex items-center">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search teams, games, leagues..."
                    className="w-48 md:w-64 bg-white/10 text-white placeholder-gray-400 border border-white/20 rounded-full px-4 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                    data-testid="search-input"
                    onKeyDown={(e) => { if (e.key === 'Escape') setSearchOpen(false); }}
                  />
                  <button onClick={() => setSearchOpen(false)} className="ml-1 text-gray-400 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button onClick={openSearch} className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid="search-toggle-btn" aria-label="Search">
                  <Search className="w-5 h-5" />
                </button>
              )}

              {/* Search Results Dropdown */}
              {searchOpen && searchQuery.length >= 2 && (
                <div className="absolute top-full right-0 mt-2 w-80 md:w-96 bg-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-700/50 max-h-96 overflow-y-auto z-50" data-testid="search-results">
                  {searchLoading ? (
                    <div className="p-4 text-center text-gray-400 text-sm">Searching...</div>
                  ) : totalResults === 0 ? (
                    <div className="p-4 text-center text-gray-400 text-sm">No results for &quot;{searchQuery}&quot;</div>
                  ) : (
                    <div className="py-2">
                      {searchResults?.teams?.length > 0 && (
                        <>
                          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-500">Teams</div>
                          {searchResults.teams.map(team => (
                            <button key={team.id} onClick={() => handleSearchSelect(`/find-game?team=${encodeURIComponent(team.name)}`)} className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-3 transition-colors" data-testid={`search-team-${team.id}`}>
                              {team.logo ? (
                                <img src={team.logo} alt="" className="w-6 h-6 rounded object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded bg-sky-800 flex items-center justify-center text-xs text-white font-bold">{team.abbreviation || team.name?.[0]}</div>
                              )}
                              <span className="text-white text-sm">{team.name}</span>
                            </button>
                          ))}
                        </>
                      )}
                      {searchResults?.leagues?.length > 0 && (
                        <>
                          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-500 border-t border-gray-800 mt-1 pt-2">Leagues</div>
                          {searchResults.leagues.map(league => (
                            <button key={league.id} onClick={() => handleSearchSelect(`/league/${league.slug}`)} className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-3 transition-colors" data-testid={`search-league-${league.id}`}>
                              {league.logo ? (
                                <img src={league.logo} alt="" className="w-6 h-6 rounded object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded bg-emerald-800 flex items-center justify-center text-xs text-white font-bold">{league.name?.[0]}</div>
                              )}
                              <span className="text-white text-sm">{league.name}</span>
                            </button>
                          ))}
                        </>
                      )}
                      {searchResults?.games?.length > 0 && (
                        <>
                          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-500 border-t border-gray-800 mt-1 pt-2">Games</div>
                          {searchResults.games.map(game => (
                            <button key={game.id} onClick={() => handleSearchSelect(`/find-game?game=${game.id}`)} className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors" data-testid={`search-game-${game.id}`}>
                              <div className="text-white text-sm">{game.home_team_name || 'TBD'} vs {game.away_team_name || 'TBD'}</div>
                              <div className="text-gray-500 text-xs mt-0.5">{game.league_name} &middot; {game.scheduled_time ? new Date(game.scheduled_time).toLocaleDateString() : ''}</div>
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* More Menu (Desktop) */}
            <div
              className="relative hidden md:block"
              onMouseEnter={() => handleDropdownEnter('more')}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-white/5 transition-colors"
                data-testid="nav-more-btn"
                aria-label="More"
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>
              {openDropdown === 'more' && <MoreDropdown />}
            </div>

            {/* Account / Sign In */}
            <div className="relative" ref={accountRef}>
              {viewer ? (
                <>
                  <button
                    onClick={() => setAccountOpen(!accountOpen)}
                    className="flex items-center gap-2 text-gray-300 hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-white/5 transition-colors"
                    data-testid="nav-account-btn"
                  >
                    <div className="w-7 h-7 rounded-full bg-sky-600 flex items-center justify-center text-white text-xs font-bold">
                      {(viewer.name || viewer.email || '?')[0].toUpperCase()}
                    </div>
                    <span className="hidden lg:inline text-sm max-w-[100px] truncate">{viewer.name || viewer.email}</span>
                    <ChevronDown className="w-3.5 h-3.5 hidden lg:block" />
                  </button>
                  {accountOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-700/50 py-2 z-50" data-testid="account-dropdown">
                      <div className="px-4 py-2 border-b border-gray-800">
                        <p className="text-white text-sm font-medium truncate">{viewer.name || 'Viewer'}</p>
                        <p className="text-gray-500 text-xs truncate">{viewer.email}</p>
                      </div>
                      <Link to="/preferences" className="flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 transition-colors text-sm" data-testid="nav-preferences-link">
                        <Settings className="w-4 h-4" /> Preferences
                      </Link>
                      <Link to="/devices" className="flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 transition-colors text-sm" data-testid="nav-devices-link">
                        <MonitorSmartphone className="w-4 h-4" /> Manage Devices
                      </Link>
                      <div className="border-t border-gray-800 mt-1 pt-1">
                        <button
                          onClick={() => { logout(); setAccountOpen(false); }}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-red-400 hover:text-red-300 hover:bg-white/5 transition-colors text-sm"
                          data-testid="nav-logout-btn"
                        >
                          <LogOut className="w-4 h-4" /> Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : viewer === false ? (
                <Link
                  to="/signup"
                  className="flex items-center gap-1.5 text-sm font-semibold text-white bg-sky-600 hover:bg-sky-500 px-4 py-1.5 rounded-full transition-colors"
                  data-testid="nav-signin-btn"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign In</span>
                </Link>
              ) : null}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* ============== MOBILE NAV ============== */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 max-h-[75vh] overflow-y-auto pb-4">
            {/* Live */}
            <Link to="/live" className={`px-4 py-3 flex items-center gap-3 ${location.pathname === '/live' ? 'text-white bg-white/10' : 'text-gray-200'}`} onClick={() => setMobileMenuOpen(false)}>
              <Radio className="w-4 h-4" /> Live
              {hasLiveGames && (
                <span className="relative flex h-2 w-2 ml-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
              )}
            </Link>

            {/* Sports — Collapsible */}
            {otherSports.map(sport => {
              const sportLeagues = getLeaguesForSport(sport.id);
              const isExpanded = mobileExpanded === sport.id;
              return (
                <div key={sport.id} className="border-t border-white/10">
                  <div className={`flex items-center ${location.pathname.includes(sport.slug) ? 'text-white bg-white/10' : 'text-gray-200'}`}>
                    <Link to={`/sport/${sport.slug}`} onClick={() => setMobileMenuOpen(false)} className="flex-1 px-4 py-3">
                      {sport.name}
                    </Link>
                    {sportLeagues.length > 0 && (
                      <button onClick={() => toggleMobileSection(sport.id)} className="px-4 py-3">
                        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>
                  {isExpanded && sportLeagues.length > 0 && (
                    <div className="bg-black/20">
                      {sportLeagues.map(league => (
                        <Link key={league.id} to={`/league/${league.slug}`} className="block px-6 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5" onClick={() => setMobileMenuOpen(false)}>
                          {league.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* GAA — Collapsible */}
            {gaaSports.length > 0 && (
              <div className="border-t border-white/10">
                <button onClick={() => toggleMobileSection('gaa')} className={`w-full px-4 py-3 flex items-center justify-between ${isGAAActive ? 'text-white bg-white/10' : 'text-gray-200'}`}>
                  <span>GAA</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === 'gaa' ? 'rotate-180' : ''}`} />
                </button>
                {mobileExpanded === 'gaa' && (
                  <div className="bg-black/20">
                    {gaaSports.map(sport => (
                      <div key={sport.id}>
                        <Link to={`/sport/${sport.slug}`} className="block px-6 py-2 text-sm text-gray-200 font-medium" onClick={() => setMobileMenuOpen(false)}>
                          {sport.name}
                        </Link>
                        {getLeaguesForSport(sport.id).map(league => (
                          <Link key={league.id} to={`/league/${league.slug}`} className="block px-8 py-1.5 text-xs text-gray-400 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                            {league.name}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Direct Links */}
            <div className="border-t border-white/10">
              <Link to="/fixtures" className="block px-4 py-3 text-gray-200 flex items-center gap-3" onClick={() => setMobileMenuOpen(false)}>
                <Calendar className="w-4 h-4" /> Schedule
              </Link>
            </div>
            <div className="border-t border-white/10">
              <Link to="/news" className="block px-4 py-3 text-gray-200 flex items-center gap-3" onClick={() => setMobileMenuOpen(false)}>
                <Newspaper className="w-4 h-4" /> News
              </Link>
            </div>
            <div className="border-t border-white/10">
              <Link to="/find-game" className="block px-4 py-3 text-gray-200 flex items-center gap-3" onClick={() => setMobileMenuOpen(false)}>
                <MapPin className="w-4 h-4" /> Find a Stream
              </Link>
            </div>
            <div className="border-t border-white/10">
              <Link to="/about" className="block px-4 py-3 text-gray-200 flex items-center gap-3" onClick={() => setMobileMenuOpen(false)}>
                <Info className="w-4 h-4" /> About Us
              </Link>
            </div>
            <div className="border-t border-white/10">
              <Link to="/radio" className="block px-4 py-3 text-gray-200 flex items-center gap-3" onClick={() => setMobileMenuOpen(false)}>
                <Headphones className="w-4 h-4" /> Radio
              </Link>
            </div>
            <div className="border-t border-white/10">
              <Link to="/devices" className="block px-4 py-3 text-gray-200 flex items-center gap-3" onClick={() => setMobileMenuOpen(false)}>
                <Smartphone className="w-4 h-4" /> Manage Devices
              </Link>
            </div>

            {/* Account Section — Mobile */}
            <div className="border-t border-white/10 mt-2 pt-2">
              {viewer ? (
                <>
                  <div className="px-4 py-2 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-sky-600 flex items-center justify-center text-white text-sm font-bold">
                      {(viewer.name || viewer.email || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{viewer.name || 'Viewer'}</p>
                      <p className="text-gray-500 text-xs">{viewer.email}</p>
                    </div>
                  </div>
                  <Link to="/preferences" className="block px-4 py-3 text-gray-200 flex items-center gap-3" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-preferences-link">
                    <Settings className="w-4 h-4" /> Preferences
                  </Link>
                  <button
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="w-full px-4 py-3 text-red-400 flex items-center gap-3 hover:bg-white/5"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </>
              ) : viewer === false ? (
                <Link to="/signup" className="block px-4 py-3 text-white flex items-center gap-3 font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-signin-link">
                  <User className="w-4 h-4" /> Sign In / Sign Up
                </Link>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* TV Mode Badge */}
      {tvMode && (
        <div className="tv-mode-badge auto-hide" data-testid="tv-mode-badge">
          <Tv className="w-4 h-4" />
          {device.name ? `${device.name} Mode` : 'TV Mode'} — Use arrow keys to navigate
        </div>
      )}
    </nav>
  );
};

export default Navigation;
